import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API_CONFIG } from "../config/api.config";
import { toast } from "react-toastify";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import {
  FaHotel,
  FaUtensils,
  FaCrown,
  FaShieldAlt,
  FaClock,
  FaWifi,
  FaCar,
  FaSwimmingPool,
  FaCoffee,
  FaConciergeBell,
  FaPhoneAlt,
  FaEnvelope,
  FaSearch,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaUsers,
} from "react-icons/fa";
import generalApi from "../api/generalApi";

const BookingHomePage = () => {
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("hotels");
  const [searchData, setSearchData] = useState({
    destination: "",
    checkIn: "",
    checkOut: "",
    guests: "2",
  });
  const [restaurantSearch, setRestaurantSearch] = useState({
    location: "",
    date: "",
    time: "",
    guests: "2",
  });
  const [siteStats, setSiteStats] = useState({
    hotels: "20+",
    restaurants: "30+",
    bookings: "2k+",
    satisfactionRate: "99.9%",
  });
  const defaultPopularLocations = [
    "Bikaner",
    "Jaipur",
    "Udaipur",
    "Delhi",
    "Mumbai",
    "Goa",
  ];
  const [popularLocations, setPopularLocations] = useState(defaultPopularLocations);

  const mergePopularLocations = (locations = []) => {
    setPopularLocations((prev) => {
      const cleaned = locations
        .map((location) => (typeof location === "string" ? location.trim() : ""))
        .filter(Boolean);
      return Array.from(new Set([...defaultPopularLocations, ...prev, ...cleaned])).slice(0, 8);
    });
  };

  useEffect(() => {
    // Fetch hotels and restaurants for data availability      
    fetchHotels();
    fetchRestaurants();
    // Fetch live site stats from backend
    generalApi.getSiteStats().then((data) => {
      setSiteStats(data);
    });
  }, []);

  const fetchHotels = async () => {
    try {
      const response = await axios.get(`${API_CONFIG.BASE_URL}/hotels`);
      const hotels = Array.isArray(response.data) ? response.data : [];
      mergePopularLocations(hotels.map((hotel) => hotel?.city || hotel?.state));
    } catch (error) {
      console.error("Error fetching hotels:", error);
    }
  };

  const fetchRestaurants = async () => {
    try {
      const response = await axios.get(`${API_CONFIG.BASE_URL}/restaurants`);
      const restaurants = Array.isArray(response.data) ? response.data : [];
      mergePopularLocations(restaurants.map((restaurant) => restaurant?.city || restaurant?.state || restaurant?.location));
    } catch (error) {
      console.error("Error fetching restaurants:", error);
    }
  };

  const handleViewRestaurants = () => navigate("/restaurants");
  const handleViewRooms = () => navigate("/HotelRoomsPage");

  const handleSearchChange = (e) => {
    const { name, value } = e.target;
    setSearchData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSearch = (e) => {
    e.preventDefault();
    
    if (activeTab === "hotels") {
      // Validate hotel search
      if (!searchData.destination.trim()) {
        toast.error("Please enter a destination");
        return;
      }
      if (!searchData.checkIn) {
        toast.error("Please select check-in date");
        return;
      }
      if (!searchData.checkOut) {
        toast.error("Please select check-out date");
        return;
      }
      if (searchData.checkOut <= searchData.checkIn) {
        toast.error("Check-out date must be after check-in date");
        return;
      }
      
      toast.success("Searching hotels...");
      navigate("/HotelRoomsPage", { state: { searchData } });
    } else if (activeTab === "restaurants") {
      // Validate restaurant search
      if (!restaurantSearch.location.trim()) {
        toast.error("Please enter a location");
        return;
      }
      if (!restaurantSearch.date) {
        toast.error("Please select a date");
        return;
      }
      if (!restaurantSearch.time) {
        toast.error("Please select dining time");
        return;
      }
      
      toast.success("Searching restaurants...");
      navigate("/restaurants", { state: { restaurantSearch } });
    }
  };

  const handleRestaurantSearchChange = (e) => {
    const { name, value } = e.target;
    setRestaurantSearch((prev) => ({ ...prev, [name]: value }));
  };

  const handleNearMe = (type) => {
    if (!navigator.geolocation) {
      toast.error("Geolocation is not supported by your browser");
      return;
    }

    toast.info("Fetching your location...");

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        let locationText = "Current Location";

        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&accept-language=en&lat=${latitude}&lon=${longitude}`,
          );

          if (response.ok) {
            const data = await response.json();
            locationText =
              data?.address?.city ||
              data?.address?.town ||
              data?.address?.village ||
              data?.address?.state ||
              locationText;
          }
        } catch (error) {
          console.error("Reverse geocoding failed:", error);
        }

        mergePopularLocations([locationText]);

        if (type === "hotels") {
          setSearchData((prev) => ({ ...prev, destination: locationText }));
        } else {
          setRestaurantSearch((prev) => ({ ...prev, location: locationText }));
        }

        toast.success(`Location detected: ${locationText}`);
      },
      (error) => {
        console.error("Error getting location:", error);
        toast.error("Unable to retrieve your location");
      }
    );
  };

  const features = [
    {
      icon: <FaShieldAlt />,
      title: "Secure Booking",
      description: "100% secure payment gateway with instant confirmation",
    },
    {
      icon: <FaCrown />,
      title: "VIP Treatment",
      description: "Exclusive perks and priority service for our members",
    },
    {
      icon: <FaClock />,
      title: "24/7 Support",
      description: "Round-the-clock customer service and assistance",
    },
  ];

  const amenities = [
    { icon: <FaWifi />, name: "Free WiFi" },
    { icon: <FaCar />, name: "Valet Parking" },
    { icon: <FaSwimmingPool />, name: "Swimming Pool" },
    { icon: <FaCoffee />, name: "Room Service" },
    { icon: <FaConciergeBell />, name: "Concierge" },
    { icon: <FaUtensils />, name: "Fine Dining" },
  ];

  const stats = [
    { number: siteStats.bookings, label: "Happy Customers" },
    { number: siteStats.hotels, label: "Premium Hotels" },
    { number: siteStats.restaurants, label: "Fine Restaurants" },
    { number: siteStats.satisfactionRate, label: "Satisfaction Rate" },
  ];

  return (
    <div
      style={{
        fontFamily: "'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
        color: "#2E2E2E",
        background: "linear-gradient(135deg, #F5F5F5 0%, #FFFFFF 100%)",
        minHeight: "100vh",
        paddingTop: "80px",
      }}
    >
      {/* Hero Section with Search Card */}
      <section
        style={{
          position: "relative",
          backgroundColor: "#000",
          backgroundSize: "cover",
          backgroundPosition: "center",
          minHeight: "600px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "60px 20px 80px",
          overflow: "hidden",
        }}
      >
        {/* Animated Background Pattern */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `url("https://images.unsplash.com/photo-1571003123894-1f0594d2b5d9?w=1920&q=80&auto=format&fit=crop")`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            opacity: 1,
            animation: "zoomInOut 25s ease-in-out infinite",
          }}
        />
        
        {/* Overlay Gradient */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "linear-gradient(to bottom, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.4) 50%, rgba(0,0,0,0.7) 100%)",
          }}
        />
        
        {/* Floating Shapes */}
        <div
          style={{
            position: "absolute",
            top: "10%",
            left: "5%",
            width: "150px",
            height: "150px",
            background: "rgba(255, 255, 255, 0.05)",
            backdropFilter: "blur(8px)",
            borderRadius: "50%",
            boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
            animation: "float 6s ease-in-out infinite",
          }}
        />
        <div
          style={{
            position: "absolute",
            bottom: "15%",
            right: "8%",
            width: "200px",
            height: "200px",
            background: "rgba(255, 255, 255, 0.05)",
            backdropFilter: "blur(8px)",
            borderRadius: "50%",
            boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
            animation: "float 8s ease-in-out infinite reverse",
          }}
        />
        
        {/* CSS Animations */}
        <style>
          {`
            @keyframes float {
              0%, 100% { transform: translateY(0px) translateX(0px); }
              50% { transform: translateY(-30px) translateX(20px); }
            }
            @keyframes zoomInOut {
              0%, 100% { transform: scale(1); }
              50% { transform: scale(1.1); }
            }
          `}
        </style>

        <div
          style={{
            maxWidth: "1200px",
            width: "100%",
            margin: "0 auto",
            position: "relative",
            zIndex: 2,
          }}
        >
          {/* Title */}
          <div
            style={{
              textAlign: "center",
              marginBottom: "40px",
              color: "#FFFFFF",
            }}
          >
            <h1
              style={{
                fontSize: "clamp(2rem, 5vw, 3.5rem)",
                fontWeight: "700",
                marginBottom: "12px",
                letterSpacing: "-0.02em",
                textShadow: "0 2px 20px rgba(0,0,0,0.2)",
              }}
            >
              Find Your Perfect Stay
            </h1>
            <p
              style={{
                fontSize: "clamp(1rem, 2vw, 1.25rem)",
                fontWeight: "400",
                opacity: 0.95,
                textShadow: "0 1px 10px rgba(0,0,0,0.2)",
              }}
            >
              World's leading chain of hotels and homes
            </p>
          </div>

          {/* Elevated Search Card */}
          <div
            style={{
              background: "#FFFFFF",
              borderRadius: "16px",
              boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
              overflow: "hidden",
              maxWidth: "1000px",
              margin: "0 auto",
            }}
          >
            {/* Tabs */}
            <div
              style={{
                display: "flex",
                borderBottom: "1px solid #E5E7EB",
                background: "#FAFAFA",
              }}
            >
              {[
                { id: "hotels", icon: <FaHotel />, label: "Hotels" },
                { id: "restaurants", icon: <FaUtensils />, label: "Restaurants" },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  style={{
                    flex: 1,
                    padding: "16px 24px",
                    border: "none",
                    background:
                      activeTab === tab.id ? "#FFFFFF" : "transparent",
                    color: activeTab === tab.id ? "#EE2E24" : "#6B7280",
                    fontWeight: activeTab === tab.id ? "700" : "600",
                    fontSize: "1rem",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                    transition: "all 0.2s ease",
                    borderBottom:
                      activeTab === tab.id
                        ? "3px solid #EE2E24"
                        : "3px solid transparent",
                    opacity: activeTab === tab.id ? 1 : 0.7,
                  }}
                  onMouseOver={(e) => {
                    if (activeTab !== tab.id) {
                      e.currentTarget.style.background = "#F5F5F5";
                      e.currentTarget.style.opacity = "1";
                    }
                  }}
                  onMouseOut={(e) => {
                    if (activeTab !== tab.id) {
                      e.currentTarget.style.background = "transparent";
                      e.currentTarget.style.opacity = "0.7";
                    }
                  }}
                >
                  <span style={{ fontSize: "1.2rem" }}>{tab.icon}</span>
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Search Form - Hotels Tab */}
            {activeTab === "hotels" && (
              <form onSubmit={handleSearch} style={{ padding: "0" }}>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns:
                      "minmax(280px, 2.5fr) minmax(140px, 1fr) minmax(140px, 1fr) minmax(100px, 0.8fr) auto",
                    gap: "0",
                    alignItems: "stretch",
                  }}
                  className="search-grid"
                >
                  {/* Destination */}
                  <div
                    style={{
                      padding: "12px 20px",
                      borderRight: "1px solid #E5E7EB",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      position: "relative",
                    }}
                  >
                    <label
                      style={{
                        fontSize: "0.7rem",
                        color: "#6B7280",
                        fontWeight: "700",
                        textTransform: "uppercase",
                        marginBottom: "4px",
                        letterSpacing: "0.5px",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        width: "100%",
                      }}
                    >
                      <span>City, Property Name or Location</span>
                    </label>
                    <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
                      <input
                        type="text"
                        name="destination"
                        value={searchData.destination}
                        onChange={handleSearchChange}
                        placeholder="Type city or hotel name"
                        style={{
                          border: "none",
                          outline: "none",
                          fontSize: "1.1rem",
                          fontWeight: "700",
                          color: "#1F2937",
                          background: "transparent",
                          width: "100%",
                          paddingRight: "10px",
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => handleNearMe("hotels")}
                        style={{
                          background: "rgba(238, 46, 36, 0.1)",
                          color: "#EE2E24",
                          border: "none",
                          borderRadius: "20px",
                          padding: "6px 12px",
                          fontSize: "0.75rem",
                          fontWeight: "700",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                          whiteSpace: "nowrap",
                          transition: "all 0.2s",
                        }}
                        onMouseOver={(e) => (e.currentTarget.style.background = "rgba(238, 46, 36, 0.2)")}
                        onMouseOut={(e) => (e.currentTarget.style.background = "rgba(238, 46, 36, 0.1)")}
                      >
                        <FaMapMarkerAlt size={10} /> Near me
                      </button>
                    </div>
                  </div>

                  {/* Check-in */}
                  <div
                    style={{
                      padding: "12px 20px",
                      borderRight: "1px solid #E5E7EB",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                    }}
                  >
                    <label
                      style={{
                        fontSize: "0.7rem",
                        color: "#6B7280",
                        fontWeight: "700",
                        textTransform: "uppercase",
                        marginBottom: "4px",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      <FaCalendarAlt size={10} /> Check-in
                    </label>
                    <input
                      type="date"
                      name="checkIn"
                      value={searchData.checkIn}
                      onChange={handleSearchChange}
                      min={new Date().toISOString().split("T")[0]}
                      style={{
                        border: "none",
                        outline: "none",
                        fontSize: "1.1rem",
                        fontWeight: "700",
                        color: "#1F2937",
                        background: "transparent",
                        width: "100%",
                        cursor: "pointer",
                        fontFamily: "inherit",
                      }}
                    />
                  </div>

                  {/* Check-out */}
                  <div
                    style={{
                      padding: "12px 20px",
                      borderRight: "1px solid #E5E7EB",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                    }}
                  >
                    <label
                      style={{
                        fontSize: "0.7rem",
                        color: "#6B7280",
                        fontWeight: "700",
                        textTransform: "uppercase",
                        marginBottom: "4px",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      <FaCalendarAlt size={10} /> Check-out
                    </label>
                    <input
                      type="date"
                      name="checkOut"
                      value={searchData.checkOut}
                      onChange={handleSearchChange}
                      min={searchData.checkIn || new Date().toISOString().split("T")[0]}
                      style={{
                        border: "none",
                        outline: "none",
                        fontSize: "1.1rem",
                        fontWeight: "700",
                        color: "#1F2937",
                        background: "transparent",
                        width: "100%",
                        cursor: "pointer",
                        fontFamily: "inherit",
                      }}
                    />
                  </div>

                  {/* Guests */}
                  <div
                    style={{
                      padding: "12px 20px",
                      borderRight: "1px solid #E5E7EB",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                    }}
                  >
                    <label
                      style={{
                        fontSize: "0.7rem",
                        color: "#6B7280",
                        fontWeight: "700",
                        textTransform: "uppercase",
                        marginBottom: "4px",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      <FaUsers size={10} /> Guests
                    </label>
                    <select
                      name="guests"
                      value={searchData.guests}
                      onChange={handleSearchChange}
                      style={{
                        border: "none",
                        outline: "none",
                        fontSize: "1.1rem",
                        fontWeight: "700",
                        color: "#1F2937",
                        background: "transparent",
                        width: "100%",
                        cursor: "pointer",
                        appearance: "none",
                      }}
                    >
                      {[1, 2, 3, 4, 5, 6, 7, 8].map((num) => (
                        <option key={num} value={num}>
                          {num} {num === 1 ? "Guest" : "Guests"}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Search Button */}
                  <button
                    type="submit"
                    style={{
                      padding: "10px 30px",
                      background: "linear-gradient(90deg, #EE2E24 0%, #FFC400 100%)",
                      color: "#FFFFFF",
                      border: "none",
                      fontSize: "1.2rem",
                      fontWeight: "700",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      textTransform: "uppercase",
                      letterSpacing: "1px",
                      transition: "all 0.3s ease",
                      minWidth: "160px",
                      gap: "8px",
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.background = "linear-gradient(90deg, #D02820 0%, #E5B000 100%)";
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.background = "linear-gradient(90deg, #EE2E24 0%, #FFC400 100%)";
                    }}
                  >
                     Search
                  </button>
                </div>

                {/* Mobile Responsive Layout - Refined & Premium */}
                <style>
                  {`
                    @media (max-width: 1024px) {
                      .search-grid {
                        display: flex !important;
                        flex-direction: column !important;
                        padding: 12px !important;
                        gap: 12px !important;
                      }
                      .search-grid > div {
                        border-right: none !important;
                        border-bottom: none !important;
                        padding: 12px 16px !important;
                        background: #F9FAFB !important;
                        border-radius: 12px !important;
                        border: 1px solid #F3F4F6 !important;
                        min-height: 64px !important;
                      }
                      .search-grid > button {
                        margin-top: 4px !important;
                        padding: 16px !important;
                        width: 100% !important;
                        border-radius: 12px !important;
                        font-size: 1.1rem !important;
                        box-shadow: 0 10px 20px rgba(238, 46, 36, 0.15) !important;
                      }
                      .popular-destinations {
                        padding: "12px 16px" !important;
                        overflow-x: auto !important;
                        white-space: nowrap !important;
                        flex-wrap: nowrap !important;
                        justify-content: flex-start !important;
                        scrollbar-width: none !important;
                        -ms-overflow-style: none !important;
                      }
                      .popular-destinations::-webkit-scrollbar {
                        display: none !important;
                      }
                      .popular-destinations span {
                        position: sticky !important;
                        left: 0 !important;
                        background: #FAFAFA !important;
                        padding-right: 8px !important;
                      }
                    }
                  `}
                </style>
                
                {/* Popular Destinations */}
                <div
                  className="popular-destinations"
                  style={{
                    padding: "12px 24px",
                    background: "#FAFAFA",
                    borderTop: "1px solid #E5E7EB",
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    flexWrap: "wrap",
                  }}
                >
                  <span
                    style={{
                      fontSize: "0.75rem",
                      color: "#6B7280",
                      fontWeight: "700",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Popular:
                  </span>
                  {popularLocations.map(
                      (destination) => (
                        <button
                          key={destination}
                          type="button"
                          onClick={() => {
                            setSearchData((prev) => ({
                              ...prev,
                              destination: destination,
                            }));
                            toast.info(`Selected ${destination}`);
                          }}
                          style={{
                            padding: "4px 12px",
                            background: "#FFFFFF",
                            color: "#4B5563",
                            border: "1px solid #E5E7EB",
                            borderRadius: "16px",
                            fontSize: "0.8rem",
                            fontWeight: "600",
                            cursor: "pointer",
                            transition: "all 0.2s ease",
                          }}
                          onMouseOver={(e) => {
                            e.target.style.color = "#EE2E24";
                            e.target.style.borderColor = "#EE2E24";
                            e.target.style.background = "#FFF5F5";
                          }}
                          onMouseOut={(e) => {
                            e.target.style.background = "#FFFFFF";
                            e.target.style.color = "#4B5563";
                            e.target.style.borderColor = "#E5E7EB";
                          }}
                        >
                          {destination}
                        </button>
                      ),
                    )}
                </div>
              </form>
            )}

            {/* Restaurants Tab */}
            {activeTab === "restaurants" && (
              <form onSubmit={handleSearch} style={{ padding: "0" }}>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns:
                      "minmax(280px, 2.5fr) minmax(140px, 1fr) minmax(140px, 1fr) minmax(100px, 0.8fr) auto",
                    gap: "0",
                    alignItems: "stretch",
                  }}
                  className="search-grid"
                >
                  {/* Location */}
                  <div
                    style={{
                      padding: "12px 20px",
                      borderRight: "1px solid #E5E7EB",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      position: "relative",
                    }}
                  >
                    <label
                      style={{
                        fontSize: "0.7rem",
                        color: "#6B7280",
                        fontWeight: "700",
                        textTransform: "uppercase",
                        marginBottom: "4px",
                        letterSpacing: "0.5px",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        width: "100%",
                      }}
                    >
                      <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        <FaMapMarkerAlt size={10} /> Location
                      </span>
                    </label>
                    <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
                      <input
                        type="text"
                        name="location"
                        value={restaurantSearch.location}
                        onChange={handleRestaurantSearchChange}
                        placeholder="Search for restaurants"
                        style={{
                          border: "none",
                          outline: "none",
                          fontSize: "1.1rem",
                          fontWeight: "700",
                          color: "#1F2937",
                          background: "transparent",
                          width: "100%",
                          paddingRight: "10px",
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => handleNearMe("restaurants")}
                        style={{
                          background: "rgba(238, 46, 36, 0.1)",
                          color: "#EE2E24",
                          border: "none",
                          borderRadius: "20px",
                          padding: "6px 12px",
                          fontSize: "0.75rem",
                          fontWeight: "700",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                          whiteSpace: "nowrap",
                          transition: "all 0.2s",
                        }}
                        onMouseOver={(e) => (e.currentTarget.style.background = "rgba(238, 46, 36, 0.2)")}
                        onMouseOut={(e) => (e.currentTarget.style.background = "rgba(238, 46, 36, 0.1)")}
                      >
                        <FaMapMarkerAlt size={10} /> Near me
                      </button>
                    </div>
                  </div>

                  {/* Date */}
                  <div
                    style={{
                      padding: "12px 20px",
                      borderRight: "1px solid #E5E7EB",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                    }}
                  >
                    <label
                      style={{
                        fontSize: "0.7rem",
                        color: "#6B7280",
                        fontWeight: "700",
                        textTransform: "uppercase",
                        marginBottom: "4px",
                        letterSpacing: "0.5px",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      <FaCalendarAlt size={10} /> Date
                    </label>
                    <input
                      type="date"
                      name="date"
                      value={restaurantSearch.date}
                      onChange={handleRestaurantSearchChange}
                      min={new Date().toISOString().split("T")[0]}
                      style={{
                        border: "none",
                        outline: "none",
                        fontSize: "1.1rem",
                        fontWeight: "700",
                        color: "#1F2937",
                        background: "transparent",
                        width: "100%",
                        cursor: "pointer",
                        fontFamily: "inherit",
                      }}
                    />
                  </div>

                  {/* Time */}
                  <div
                    style={{
                      padding: "12px 20px",
                      borderRight: "1px solid #E5E7EB",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                    }}
                  >
                    <label
                      style={{
                        fontSize: "0.7rem",
                        color: "#6B7280",
                        fontWeight: "700",
                        textTransform: "uppercase",
                        marginBottom: "4px",
                        letterSpacing: "0.5px",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      <FaClock size={10} /> Time
                    </label>
                    <select
                      name="time"
                      value={restaurantSearch.time}
                      onChange={handleRestaurantSearchChange}
                      style={{
                        border: "none",
                        outline: "none",
                        fontSize: "1.1rem",
                        fontWeight: "700",
                        color: "#1F2937",
                        background: "transparent",
                        width: "100%",
                        cursor: "pointer",
                        appearance: "none",
                      }}
                    >
                      <option value="">Select Time</option>
                      <option value="12:00">12:00 PM</option>
                      <option value="12:30">12:30 PM</option>
                      <option value="13:00">01:00 PM</option>
                      <option value="13:30">01:30 PM</option>
                      <option value="14:00">02:00 PM</option>
                      <option value="19:00">07:00 PM</option>
                      <option value="19:30">07:30 PM</option>
                      <option value="20:00">08:00 PM</option>
                      <option value="20:30">08:30 PM</option>
                      <option value="21:00">09:00 PM</option>
                      <option value="21:30">09:30 PM</option>
                    </select>
                  </div>

                  {/* Guests */}
                  <div
                    style={{
                      padding: "12px 20px",
                      borderRight: "1px solid #E5E7EB",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                    }}
                  >
                    <label
                      style={{
                        fontSize: "0.7rem",
                        color: "#6B7280",
                        fontWeight: "700",
                        textTransform: "uppercase",
                        marginBottom: "4px",
                        letterSpacing: "0.5px",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      <FaUsers size={10} /> Guests
                    </label>
                    <select
                      name="guests"
                      value={restaurantSearch.guests}
                      onChange={handleRestaurantSearchChange}
                      style={{
                        border: "none",
                        outline: "none",
                        fontSize: "1.1rem",
                        fontWeight: "700",
                        color: "#1F2937",
                        background: "transparent",
                        width: "100%",
                        cursor: "pointer",
                        appearance: "none",
                      }}
                    >
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                        <option key={num} value={num}>
                          {num} {num === 1 ? "Guest" : "Guests"}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Search Button */}
                  <button
                    type="submit"
                    style={{
                      padding: "10px 30px",
                      background:
                        "linear-gradient(90deg, #EE2E24 0%, #FFC400 100%)",
                      color: "#FFFFFF",
                      border: "none",
                      fontSize: "1.2rem",
                      fontWeight: "700",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      textTransform: "uppercase",
                      letterSpacing: "1px",
                      transition: "all 0.3s ease",
                      minWidth: "160px",
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.background =
                        "linear-gradient(90deg, #D02820 0%, #E5B000 100%)";
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.background =
                        "linear-gradient(90deg, #EE2E24 0%, #FFC400 100%)";
                    }}
                  >
                    Search
                  </button>
                </div>

                {/* Mobile Responsive Styles */}
                <style>
                  {`
                    @media (max-width: 1024px) {
                      .search-grid {
                        display: flex !important;
                        flex-direction: column !important;
                      }
                      .search-grid > div {
                        border-right: none !important;
                        border-bottom: 1px solid #E5E7EB !important;
                        padding: 16px 20px !important;
                      }
                      .search-grid > button {
                        padding: 20px !important;
                        width: 100% !important;
                        border-radius: 0 0 16px 16px !important;
                      }
                    }
                  `}
                </style>
                
                {/* Popular Cuisines & Locations */}
                <div
                  style={{
                    padding: "12px 24px",
                    background: "#FAFAFA",
                    borderTop: "1px solid #E5E7EB",
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    flexWrap: "wrap",
                  }}
                >
                  <span
                    style={{
                      fontSize: "0.75rem",
                      color: "#6B7280",
                      fontWeight: "700",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Popular:
                  </span>
                  <div
                    style={{
                      display: "flex",
                      gap: "8px",
                      flexWrap: "wrap",
                    }}
                  >
                    {[
                      { label: "Italian", emoji: "🍝" },
                      { label: "Chinese", emoji: "🥢" },
                      { label: "Indian", emoji: "🍛" },
                      { label: "Continental", emoji: "🍴" },
                      { label: "Fast Food", emoji: "🍔" },
                    ].map((item) => (
                      <button
                        key={item.label}
                        type="button"
                        onClick={() => {
                          toast.info(`Searching ${item.label} restaurants...`);
                        }}
                        style={{
                          padding: "4px 12px",
                          background: "#FFFFFF",
                          color: "#4B5563",
                          border: "1px solid #E5E7EB",
                          borderRadius: "16px",
                          fontSize: "0.8rem",
                          fontWeight: "600",
                          cursor: "pointer",
                          transition: "all 0.2s ease",
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                        }}
                        onMouseOver={(e) => {
                          e.currentTarget.style.color = "#EE2E24";
                          e.currentTarget.style.borderColor = "#EE2E24";
                          e.currentTarget.style.background = "#FFF5F5";
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.background = "#FFFFFF";
                          e.currentTarget.style.color = "#4B5563";
                          e.currentTarget.style.borderColor = "#E5E7EB";
                        }}
                      >
                        <span>{item.emoji}</span>
                        {item.label}
                      </button>
                    ))}
                  </div>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Premium Features Section */}
      <section
        style={{
          padding: "clamp(30px, 8vw, 60px) clamp(15px, 3vw, 20px)",
          background: "linear-gradient(135deg, #FFFFFF 0%, #F5F5F5 100%)",
          position: "relative",
        }}
      >
        <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
          <div
            style={{
              textAlign: "center",
              marginBottom: "clamp(25px, 5vw, 40px)",
            }}
          >
            <h2
              style={{
                fontSize: "clamp(1.5rem, 5vw, 2rem)",
                fontWeight: "700",
                background: "linear-gradient(135deg, #EE2E24 0%, #FFC400 100%)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                color: "transparent",
                marginBottom: "clamp(8px, 2vw, 12px)",
                letterSpacing: "-0.5px",
                textTransform: "uppercase",
                fontFamily: "'Inter', sans-serif",
              }}
            >
              Why Choose Trivana
            </h2>
            <p
              style={{
                fontSize: "clamp(0.9rem, 2.5vw, 1rem)",
                color: "#666",
                maxWidth: "550px",
                margin: "0 auto",
                lineHeight: "1.5",
                fontWeight: "400",
                letterSpacing: "0.3px",
                padding: "0 15px",
              }}
            >
              Experience luxury with our curated premium services
            </p>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns:
                "repeat(auto-fit, minmax(min(100%, 280px), 1fr))",
              gap: "clamp(15px, 3vw, 25px)",
              marginBottom: "clamp(30px, 6vw, 50px)",
            }}
          >
            {features.map((feature, index) => (
              <div
                key={index}
                style={{
                  background: "rgba(255,255,255,0.8)",
                  backdropFilter: "blur(20px)",
                  padding: "clamp(20px, 4vw, 30px)",
                  borderRadius: "16px",
                  textAlign: "center",
                  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                  border: "1px solid rgba(255,255,255,0.2)",
                  boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
                  cursor: "pointer",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform =
                    "translateY(-10px) scale(1.02)";
                  e.currentTarget.style.boxShadow =
                    "0 25px 50px rgba(0,0,0,0.15)";
                  e.currentTarget.style.background = "rgba(255,255,255,0.95)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0) scale(1)";
                  e.currentTarget.style.boxShadow =
                    "0 10px 40px rgba(0,0,0,0.08)";
                  e.currentTarget.style.background = "rgba(255,255,255,0.8)";
                }}
              >
                <div
                  style={{
                    fontSize: "3rem",
                    color: "#EE2E24",
                    marginBottom: "20px",
                    filter: "drop-shadow(0 4px 8px rgba(238, 46, 36, 0.2))",
                  }}
                >
                  {feature.icon}
                </div>
                <h3
                  style={{
                    fontSize: "clamp(1rem, 3vw, 1.15rem)",
                    fontWeight: "700",
                    color: "#2E2E2E",
                    marginBottom: "clamp(8px, 2vw, 10px)",
                    letterSpacing: "-0.3px",
                    textTransform: "uppercase",
                    fontFamily: "'Inter', sans-serif",
                  }}
                >
                  {feature.title}
                </h3>
                <p
                  style={{
                    color: "#666",
                    fontSize: "clamp(0.85rem, 2vw, 0.9rem)",
                    lineHeight: "1.5",
                    fontWeight: "400",
                    letterSpacing: "0.2px",
                  }}
                >
                  {feature.description}
                </p>
              </div>
            ))}
          </div>

          {/* Stats Section */}
          <div
            style={{
              background: "linear-gradient(135deg, #EE2E24 0%, #2E2E2E 100%)",
              borderRadius: "16px",
              padding: "clamp(25px, 5vw, 40px) clamp(20px, 4vw, 30px)",
              color: "white",
              textAlign: "center",
            }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns:
                  "repeat(auto-fit, minmax(min(100%, 150px), 1fr))",
                gap: "clamp(20px, 4vw, 40px)",
              }}
            >
              {stats.map((stat, index) => (
                <div key={index}>
                  <div
                    style={{
                      fontSize: "clamp(1.8rem, 6vw, 2.5rem)",
                      fontWeight: "800",
                      marginBottom: "clamp(5px, 1.5vw, 8px)",
                      color: "#ffffff",
                      letterSpacing: "-1px",
                      fontFamily: "'Inter', sans-serif",
                    }}
                  >
                    {stat.number}
                  </div>
                  <div
                    style={{
                      fontSize: "clamp(0.8rem, 2vw, 0.95rem)",
                      color: "rgba(255,255,255,0.9)",
                      fontWeight: "500",
                      letterSpacing: "0.5px",
                      textTransform: "uppercase",
                    }}
                  >
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Premium Amenities Section */}
      <section
        style={{
          padding: "clamp(30px, 8vw, 60px) clamp(15px, 3vw, 20px)",
          background: "linear-gradient(135deg, #F5F5F5 0%, #FFFFFF 100%)",
        }}
      >
        <div
          style={{ maxWidth: "1200px", margin: "0 auto", textAlign: "center" }}
        >
          <h2
            style={{
              fontSize: "clamp(1.5rem, 5vw, 2rem)",
              fontWeight: "700",
              color: "#2E2E2E",
              marginBottom: "clamp(8px, 2vw, 12px)",
              letterSpacing: "-0.5px",
              textTransform: "uppercase",
              fontFamily: "'Inter', sans-serif",
            }}
          >
            Premium Amenities
          </h2>
          <p
            style={{
              fontSize: "clamp(0.9rem, 2.5vw, 1rem)",
              color: "#666",
              marginBottom: "clamp(25px, 5vw, 40px)",
              maxWidth: "550px",
              margin: "0 auto clamp(25px, 5vw, 40px) auto",
              fontWeight: "400",
              letterSpacing: "0.3px",
              lineHeight: "1.5",
              padding: "0 15px",
            }}
          >
            World-class amenities for your comfort
          </p>

          <div
            style={{
              display: "grid",
              gridTemplateColumns:
                "repeat(auto-fit, minmax(min(100%, 250px), 1fr))",
              gap: "clamp(15px, 3vw, 25px)",
            }}
          >
            {amenities.map((amenity, index) => (
              <div
                key={index}
                style={{
                  background: "rgba(255,255,255,0.9)",
                  backdropFilter: "blur(10px)",
                  padding: "clamp(20px, 4vw, 30px) clamp(18px, 3vw, 25px)",
                  borderRadius: "16px",
                  display: "flex",
                  alignItems: "center",
                  gap: "clamp(12px, 3vw, 20px)",
                  transition: "all 0.3s ease",
                  border: "1px solid rgba(255,255,255,0.2)",
                  boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
                  cursor: "pointer",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform =
                    "translateY(-8px) scale(1.02)";
                  e.currentTarget.style.boxShadow =
                    "0 15px 50px rgba(0,0,0,0.15)";
                  e.currentTarget.style.background = "rgba(255,255,255,0.95)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0) scale(1)";
                  e.currentTarget.style.boxShadow =
                    "0 8px 32px rgba(0,0,0,0.08)";
                  e.currentTarget.style.background = "rgba(255,255,255,0.9)";
                }}
              >
                <div
                  style={{
                    fontSize: "clamp(2rem, 5vw, 2.5rem)",
                    color: "#EE2E24",
                    minWidth: "clamp(50px, 10vw, 70px)",
                    textAlign: "center",
                    filter: "drop-shadow(0 2px 4px rgba(238, 46, 36, 0.2))",
                  }}
                >
                  {amenity.icon}
                </div>
                <span
                  style={{
                    fontSize: "clamp(0.9rem, 2.5vw, 1.05rem)",
                    fontWeight: "700",
                    color: "#2E2E2E",
                    letterSpacing: "-0.2px",
                    textTransform: "uppercase",
                    fontFamily: "'Inter', sans-serif",
                  }}
                >
                  {amenity.name}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section
        style={{
          padding: "clamp(30px, 8vw, 60px) clamp(15px, 3vw, 20px)",
          background: "linear-gradient(135deg, #FFFFFF 0%, #F5F5F5 100%)",
          textAlign: "center",
        }}
      >
        <div style={{ maxWidth: "800px", margin: "0 auto" }}>
          <h2
            style={{
              fontSize: "clamp(1.5rem, 5vw, 2rem)",
              fontWeight: "700",
              color: "#2E2E2E",
              marginBottom: "clamp(8px, 2vw, 12px)",
              letterSpacing: "-0.5px",
              textTransform: "uppercase",
              fontFamily: "'Inter', sans-serif",
            }}
          >
            Ready for Your Next Adventure?
          </h2>
          <p
            style={{
              fontSize: "clamp(0.9rem, 2.5vw, 1rem)",
              color: "#666",
              marginBottom: "clamp(25px, 5vw, 40px)",
              lineHeight: "1.5",
              fontWeight: "400",
              letterSpacing: "0.3px",
              maxWidth: "550px",
              margin: "0 auto clamp(25px, 5vw, 40px) auto",
              padding: "0 15px",
            }}
          >
            Join thousands who trust Trivana. Book your perfect experience
            today.
          </p>

          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "clamp(10px, 3vw, 20px)",
              flexWrap: "wrap",
              marginBottom: "clamp(30px, 8vw, 60px)",
            }}
          >
            <button
              onClick={handleViewRooms}
              style={{
                background: "linear-gradient(135deg, #EE2E24 0%, #FFC400 100%)",
                color: "white",
                border: "none",
                padding: "clamp(14px, 3vw, 18px) clamp(24px, 5vw, 36px)",
                borderRadius: "12px",
                fontSize: "clamp(14px, 2.5vw, 16px)",
                fontWeight: "600",
                cursor: "pointer",
                transition: "all 0.3s ease",
                boxShadow: "0 10px 25px rgba(238, 46, 36, 0.3)",
                width: "auto",
                minWidth: "fit-content",
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = "translateY(-3px)";
                e.target.style.boxShadow = "0 15px 35px rgba(238, 46, 36, 0.4)";
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = "translateY(0)";
                e.target.style.boxShadow = "0 10px 25px rgba(238, 46, 36, 0.3)";
              }}
            >
              Book a Hotel
            </button>
            <button
              onClick={handleViewRestaurants}
              style={{
                background: "linear-gradient(135deg, #2E2E2E 0%, #FFC400 100%)",
                color: "white",
                border: "none",
                padding: "clamp(14px, 3vw, 18px) clamp(24px, 5vw, 36px)",
                borderRadius: "12px",
                fontSize: "clamp(14px, 2.5vw, 16px)",
                fontWeight: "600",
                cursor: "pointer",
                transition: "all 0.3s ease",
                boxShadow: "0 10px 25px rgba(46, 46, 46, 0.3)",
                width: "auto",
                minWidth: "fit-content",
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = "translateY(-3px)";
                e.target.style.boxShadow = "0 15px 35px rgba(255, 196, 0, 0.4)";
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = "translateY(0)";
                e.target.style.boxShadow = "0 10px 25px rgba(46, 46, 46, 0.3)";
              }}
            >
              Reserve a Table
            </button>
          </div>

          {/* Contact Info */}
          <div
            style={{
              background: "rgba(238, 46, 36, 0.05)",
              borderRadius: "12px",
              padding: "clamp(18px, 4vw, 25px)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: "clamp(15px, 4vw, 30px)",
              flexWrap: "wrap",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <FaPhoneAlt
                style={{
                  color: "#EE2E24",
                  fontSize: "clamp(0.95rem, 2.5vw, 1.1rem)",
                }}
              />
              <span
                style={{
                  color: "#2E2E2E",
                  fontWeight: "700",
                  fontSize: "clamp(0.85rem, 2vw, 0.95rem)",
                  letterSpacing: "0.3px",
                  fontFamily: "'Inter', sans-serif",
                }}
              >
                +91 9257734350
              </span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <FaEnvelope
                style={{
                  color: "#EE2E24",
                  fontSize: "clamp(0.95rem, 2.5vw, 1.1rem)",
                }}
              />
              <span
                style={{
                  color: "#2E2E2E",
                  fontWeight: "700",
                  fontSize: "clamp(0.85rem, 2vw, 0.95rem)",
                  letterSpacing: "0.3px",
                  fontFamily: "'Inter', sans-serif",
                }}
              >
                booktrivana@gmail.com
              </span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BookingHomePage;
